import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Search, User, Stethoscope, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export interface SelectOption {
  id: string;
  full_name: string;
  email: string;
  specialty?: string;
  phone?: string;
  [key: string]: any;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value?: SelectOption;
  onSelect: (option: SelectOption) => void;
  onSearch?: (query: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  type: 'patient' | 'practitioner';
  loading?: boolean;
  disabled?: boolean;
  error?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onSelect,
  onSearch,
  placeholder,
  searchPlaceholder,
  type,
  loading = false,
  disabled = false,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (query: string) => {
    if (onSearch) {
      onSearch(query);
    } else {
      // Local filtering
      const filtered = options.filter(option =>
        option.full_name.toLowerCase().includes(query.toLowerCase()) ||
        option.email.toLowerCase().includes(query.toLowerCase()) ||
        (option.specialty && option.specialty.toLowerCase().includes(query.toLowerCase()))
      );
      setFilteredOptions(filtered);
    }
  };

  const handleSelect = (option: SelectOption) => {
    onSelect(option);
    setIsOpen(false);
    setSearchQuery('');
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  const getIcon = (isSelected?: boolean) => {
    const iconClass = cn('h-4 w-4', isSelected ? 'text-brand-teal' : 'text-slate-500');
    return type === 'patient' 
      ? <User className={iconClass} /> 
      : <Stethoscope className={iconClass} />;
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);


  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        type="button"
        variant="outline"
        onClick={toggleDropdown}
        disabled={disabled}
        className={cn(
          "w-full justify-between text-left font-normal h-10 border-slate-200 bg-white hover:bg-slate-50",
          !value && "text-slate-500",
          value && "text-slate-900",
          error && "border-red-500",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
            value ? "bg-brand-teal/10" : "bg-slate-100"
          )}>
            {type === 'patient' 
              ? <User className={cn("h-3 w-3", value ? "text-brand-teal" : "text-slate-400")} /> 
              : <Stethoscope className={cn("h-3 w-3", value ? "text-brand-teal" : "text-slate-400")} />
            }
          </div>
          <span className={cn("truncate text-sm", value ? "font-medium" : "")}>
            {value ? value.full_name : placeholder}
          </span>
        </div>
        <ChevronDown 
          className={cn(
            "h-4 w-4 text-slate-400 transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </Button>

      {error && (
        <p className="text-[0.8rem] text-red-500 mt-1">{error}</p>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute z-[450] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-slate-100 bg-slate-50/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 bg-white border-slate-200 text-sm placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-brand-teal focus-visible:border-brand-teal"
                />
                {loading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 text-brand-teal animate-spin" />
                  </div>
                )}
              </div>
            </div>

            {/* Options List */}
            <div className="max-h-[280px] overflow-y-auto">
              {loading && filteredOptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 px-4">
                  <Loader2 className="h-7 w-7 text-brand-teal animate-spin mb-2" />
                  <p className="text-sm text-slate-500">Loading...</p>
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 px-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                    {type === 'patient' 
                      ? <User className="h-6 w-6 text-slate-300" />
                      : <Stethoscope className="h-6 w-6 text-slate-300" />
                    }
                  </div>
                  <p className="text-sm font-medium text-slate-700">
                    No {type}s found
                  </p>
                  {searchQuery && (
                    <p className="text-xs text-slate-500 mt-1">
                      No results for "{searchQuery}"
                    </p>
                  )}
                </div>
              ) : (
                <div className="py-1">
                  {filteredOptions.map((option) => {
                    const isSelected = value?.id === option.id;
                    return (
                      <button
                        type="button"
                        key={option.id}
                        onClick={() => handleSelect(option)}
                        className={cn(
                          "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                          isSelected
                            ? "bg-brand-teal/5 border-l-2 border-brand-teal"
                            : "hover:bg-slate-50 border-l-2 border-transparent"
                        )}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                          isSelected ? "bg-brand-teal/10" : "bg-slate-100"
                        )}>
                          {type === 'patient' 
                            ? <User className={cn("h-4 w-4", isSelected ? "text-brand-teal" : "text-slate-500")} />
                            : <Stethoscope className={cn("h-4 w-4", isSelected ? "text-brand-teal" : "text-slate-500")} />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "font-medium text-sm truncate",
                              isSelected ? "text-brand-teal" : "text-slate-900"
                            )}>
                              {option.full_name}
                            </span>
                            {isSelected && (
                              <Check className="h-4 w-4 text-brand-teal flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs text-slate-500 truncate">{option.email}</span>
                            {option.specialty && (
                              <>
                                <span className="text-slate-300">•</span>
                                <Badge 
                                  variant="secondary" 
                                  className="text-[10px] px-1.5 py-0 h-4 bg-slate-100 hover:bg-slate-100 text-slate-600 font-medium"
                                >
                                  {option.specialty}
                                </Badge>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchableSelect; 